import { Box } from '@palmetto/palmetto-components';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { NavLink, Route, Routes, useNavigate, useParams } from 'react-router-dom';

import { AdminTask, AdminTaskMap } from 'types';

import usePermissions from '../../hooks/usePermissions';
import RequirePermissions from '../auth/requirePermissions';
import { MainContainer } from '../MainContainer';
import PageHeader from '../PageHeader';

import { getFirstAccessibleTaskKey } from './getFirstAccessibleTaskKey';
import { Task } from './Task';

export const AdminTasks = () => {
  const navigate = useNavigate();

  const { taskType } = useParams<{ taskType: AdminTask }>();
  const { replayFailedDirectPayLogsFlag = false } = useFlags();
  const userPermissions = usePermissions();

  // The replay task is gated by a feature flag — hide it from the menu when off.
  const visibleTaskEntries = Object.entries(AdminTaskMap).filter(
    ([key]) => key !== AdminTask.replayFailedDirectPayLogs || replayFailedDirectPayLogsFlag,
  );

  // First task the user can actually open — used for the default redirect and the
  // "Back to Tasks" link so we never land on a task gated out of their menu.
  const fallbackTaskKey = getFirstAccessibleTaskKey(userPermissions, { replayFailedDirectPayLogsFlag });

  useEffect(() => {
    if (!taskType && fallbackTaskKey) {
      navigate(`${fallbackTaskKey}`);
    }
  }, [taskType, navigate, fallbackTaskKey]);

  // This task streams logs and a history table, so it gets the full width with
  // the task menu hidden and a link back to the menu instead.
  const isFullPageTask =
    taskType === AdminTask.replayFailedDirectPayLogs || taskType === AdminTask.replayFailedConcordLogs;

  return (
    <>
      <Helmet>
        <title>Admin Tasks</title>
      </Helmet>
      <MainContainer maxWidth="100">
        <PageHeader title="Admin Tasks"></PageHeader>
        <Box direction="row" childGap="3xl">
          {!isFullPageTask && (
            <Box>
              {visibleTaskEntries.map(([key, task]) => (
                <RequirePermissions permissions={task.neededPermissions} checkAllPermissions key={key}>
                  <NavLink to={`/admin/tasks/${key}`} style={{ textDecoration: 'none', fontWeight: '500' }} end>
                    {({ isActive }) => (
                      <Box
                        as="span"
                        padding="md"
                        className={isActive ? 'tab-active' : ''}
                        hover={{ color: 'primary' }}
                        style={{ transitionDuration: '0s', borderBottom: 0 }}
                      >
                        {task.title}
                      </Box>
                    )}
                  </NavLink>
                </RequirePermissions>
              ))}
            </Box>
          )}
          <Box flex="auto">
            {isFullPageTask && (
              <Box margin="0 0 md 0">
                <NavLink
                  to={fallbackTaskKey ? `/admin/tasks/${fallbackTaskKey}` : '/admin/tasks'}
                  style={{ textDecoration: 'none', fontWeight: '500' }}
                >
                  ← Back to Tasks
                </NavLink>
              </Box>
            )}
            <Routes>
              <Route path=":taskType?" element={<Task />} />
            </Routes>
          </Box>
        </Box>
      </MainContainer>
    </>
  );
};
